/**
 * DroneTestMode - Real-time frame stream simulator
 * Loads and streams roof inspection frames continuously like a live video feed
 */
export class DroneTestMode {
  private intervalId: number | null = null;
  private frameUrls: string[] = [];
  private currentIndex = 0;
  private running = false;
  private fps = 2; // Reduced from 10 to 2 FPS for scalability

  constructor() {
    this.loadFrameUrls();
  }

  /**
   * Load all test drone feed frames from directory
   */
  private loadFrameUrls(): void {
    // Only load the 24 frames we actually have
    const frameCount = 24;
    this.frameUrls = [];
    
    for (let i = 1; i <= frameCount; i++) {
      const frameNum = String(i).padStart(4, '0');
      this.frameUrls.push(`/test_drone_feed/frame_${frameNum}.jpg`);
    }

    console.log(`📸 Loaded ${frameCount} frame URLs for test feed`);
  }

  /**
   * Start continuous frame stream at specified FPS
   * Simulates a live video feed
   */
  startTestFeed(onFrame: (imageUrl: string) => void, fps: number = 2): void {
    if (this.running) {
      console.warn('⚠️ Test feed already running');
      return;
    }

    console.log(`🎬 Starting real-time test feed at ${fps} FPS`);
    this.fps = fps;
    this.running = true;
    this.currentIndex = 0;

    const interval = 1000 / fps;

    // Emit first frame immediately
    this.emitCurrentFrame(onFrame);

    // Start continuous stream
    this.intervalId = window.setInterval(() => {
      this.currentIndex = (this.currentIndex + 1) % this.frameUrls.length;
      this.emitCurrentFrame(onFrame);
    }, interval);
  }

  /**
   * Emit current frame - simplified without async checks
   */
  private emitCurrentFrame(onFrame: (imageUrl: string) => void): void {
    const url = this.frameUrls[this.currentIndex];
    onFrame(url);
  }

  /**
   * Stop the frame stream
   */
  stopTestFeed(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.running = false;
    this.currentIndex = 0;
    console.log('🛑 Test feed stream stopped');
  }

  /**
   * Check if stream is running
   */
  isRunning(): boolean {
    return this.running;
  }

  /**
   * Get current frame URL
   */
  getCurrentImageUrl(): string {
    return this.frameUrls[this.currentIndex] || '';
  }

  /**
   * Update stream FPS (requires restart)
   */
  setFPS(fps: number): void {
    if (fps < 1 || fps > 30) {
      console.warn('FPS must be between 1-30');
      return;
    }
    
    this.fps = fps;
    
    if (this.running) {
      console.log(`🔄 Updating FPS to ${fps} (restart required)`);
    }
  }

  /**
   * Get stream statistics
   */
  getStats(): { totalFrames: number; currentFrame: number; fps: number; running: boolean } {
    return {
      totalFrames: this.frameUrls.length,
      currentFrame: this.currentIndex + 1,
      fps: this.fps,
      running: this.running
    };
  }
}
